import React from "react";
import { cn } from "../cn";

export type ProgressTone = "accent" | "success" | "warning" | "critical" | "info";

export type ProgressSegment = {
  value: number;
  tone: ProgressTone;
};

export type ProgressBarProps = {
  /**
   * Accessible name for the bar. Required: a progress bar with no name is a
   * decorative stripe to a screen reader (WCAG 4.1.2).
   */
  label: string;
  /** One or more stacked segments, e.g. completed then in-progress. */
  segments: ProgressSegment[];
  max: number;
  /** Visible text describing the same figure, e.g. "3/8". */
  valueText?: string;
  size?: "sm" | "md";
  className?: string;
};

const TONE_FILL: Record<ProgressTone, string> = {
  accent: "bg-accent",
  success: "bg-status-success-fill",
  warning: "bg-status-warning-fill",
  critical: "bg-status-critical-fill",
  info: "bg-status-info-fill",
};

/**
 * A stacked progress bar.
 *
 * The app drew these as bare divs with an animated width, which meant the only
 * thing conveying "3 of 8 agreements complete" was the length of a coloured
 * rectangle — inaccessible, and unreadable at a glance even sighted, which is
 * why `valueText` sits beside it.
 *
 * Width is animated with a CSS transition rather than Framer Motion so the
 * global prefers-reduced-motion rule already covers it.
 */
const ProgressBar: React.FC<ProgressBarProps> = ({
  label,
  segments,
  max,
  valueText,
  size = "md",
  className,
}) => {
  const safeMax = max > 0 ? max : 1;
  const total = segments.reduce((sum, segment) => sum + segment.value, 0);

  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuenow={total}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-valuetext={valueText}
      className={cn(
        "flex w-full gap-0.5 overflow-hidden rounded-full bg-surface-sunken",
        size === "sm" ? "h-1.5" : "h-2.5",
        className
      )}
    >
      {segments.map((segment, index) => (
        <span
          key={`${segment.tone}-${index}`}
          className={cn(
            "h-full transition-[width] duration-[--duration-slow] ease-[--ease-standard] first:rounded-l-full last:rounded-r-full",
            TONE_FILL[segment.tone]
          )}
          style={{ width: `${Math.min((segment.value / safeMax) * 100, 100)}%` }}
        />
      ))}
    </div>
  );
};

export default ProgressBar;
